import type { QuizDefinition, QuizQuestion } from "@quiz/quiz-core";
import { embeddedQuestionShadowStyles, renderEmbeddedQuestion } from "../components/EmbeddedQuestion";
import type { ResolvedUiCopy } from "../ui/copy";
import { el } from "../utils";
import { renderNativeStartScreen } from "./StartScreen";

export interface CustomStartOptions {
  quiz: QuizDefinition;
  copy: ResolvedUiCopy;
  firstQuestion?: QuizQuestion;
  selectedAnswerId?: string;
  progress?: number;
  onStart: () => void;
  onAnswer: (answerId: string, button: HTMLButtonElement) => void;
}

export function renderCustomStartScreen(options: CustomStartOptions): HTMLElement {
  const screen = el("main", "screen custom-start-screen");
  const host = el("div", "custom-start-host");
  screen.append(host);
  const shadow = host.attachShadow({ mode: "open" });
  shadow.append(baseShadowStyle(), loadingNode());
  void loadCustomStart(options, shadow, screen);
  return screen;
}

async function loadCustomStart(options: CustomStartOptions, shadow: ShadowRoot, screen: HTMLElement): Promise<void> {
  const config = options.quiz.start.customHtml;
  if (!config) {
    fallback(options, screen, "Custom HTML config is missing");
    return;
  }

  try {
    const htmlResponse = await fetch(config.htmlUrl);
    if (!htmlResponse.ok) throw new Error(`Could not load ${config.htmlUrl}: ${htmlResponse.status}`);
    const html = await htmlResponse.text();
    const fragment = parseHtmlFragment(html);
    if (!hasEngineHooks(fragment)) {
      throw new Error(`Custom HTML ${config.htmlUrl} does not contain quiz hooks`);
    }

    shadow.replaceChildren(baseShadowStyle(), embeddedStyle());
    for (const cssUrl of config.cssUrls ?? []) {
      const cssResponse = await fetch(cssUrl);
      if (!cssResponse.ok) throw new Error(`Could not load ${cssUrl}: ${cssResponse.status}`);
      const style = document.createElement("style");
      style.textContent = await cssResponse.text();
      shadow.append(style);
    }
    shadow.append(fragment);
    wireHooks(options, shadow);
  } catch (error) {
    fallback(options, screen, error instanceof Error ? error.message : String(error));
  }
}

function parseHtmlFragment(html: string): DocumentFragment {
  const parsed = new DOMParser().parseFromString(html, "text/html");
  parsed.querySelectorAll("script").forEach((script) => script.remove());
  const fragment = document.createDocumentFragment();
  const nodes = parsed.body.childNodes.length > 0 ? parsed.body.childNodes : parsed.childNodes;
  fragment.append(...Array.from(nodes).map((node) => document.importNode(node, true)));
  return fragment;
}

function hasEngineHooks(fragment: DocumentFragment): boolean {
  return Boolean(fragment.querySelector("[data-quiz-action], [data-quiz-slot]"));
}

function wireHooks(options: CustomStartOptions, shadow: ShadowRoot): void {
  shadow.querySelectorAll<HTMLElement>("[data-quiz-action='start']").forEach((node) => {
    node.addEventListener("click", options.onStart);
  });

  shadow.querySelectorAll<HTMLElement>("[data-quiz-slot='logo']").forEach((slot) => {
    slot.replaceChildren();
    if (options.quiz.start.logo?.src) {
      const logo = document.createElement("img");
      logo.src = options.quiz.start.logo.src;
      logo.alt = options.quiz.start.logo.alt ?? options.quiz.metadata.name;
      logo.className = "custom-quiz-logo";
      slot.append(logo);
    }
  });

  shadow.querySelectorAll<HTMLElement>("[data-quiz-slot='progress']").forEach((slot) => {
    const progress = el("div", "custom-progress");
    const bar = el("div", "custom-progress__bar");
    bar.style.width = `${options.progress ?? 0}%`;
    progress.append(bar);
    slot.replaceChildren(progress);
  });

  shadow.querySelectorAll<HTMLElement>("[data-quiz-slot='first-question']").forEach((slot) => {
    slot.replaceChildren();
    if (!options.firstQuestion) return;
    slot.append(
      renderEmbeddedQuestion({
        quiz: options.quiz,
        question: options.firstQuestion,
        selectedAnswerId: options.selectedAnswerId,
        progress: options.progress,
        compact: true,
        visual: options.firstQuestion.answers.some((answer) => answer.media),
        onAnswer: options.onAnswer
      })
    );
  });
}

function fallback(options: CustomStartOptions, screen: HTMLElement, message: string): void {
  console.error(`[custom-start] ${message}`);
  if (options.quiz.start.customHtml?.fallback === "none") {
    screen.replaceChildren(el("p", "custom-start-error", "Start screen could not be loaded."));
    return;
  }
  screen.replaceWith(renderNativeStartScreen({ ...options, quiz: { ...options.quiz, start: { ...options.quiz.start, renderer: "native" } } }));
}

function loadingNode(): HTMLElement {
  const node = el("div", "custom-start-loading", "Loading...");
  return node;
}

function baseShadowStyle(): HTMLStyleElement {
  const style = document.createElement("style");
  style.textContent = `
    :host{display:block;min-height:100svh}
    *,*::before,*::after{box-sizing:border-box}
    .custom-start-loading{min-height:100svh;display:grid;place-items:center;font-family:system-ui,sans-serif;color:#102033}
    .custom-quiz-logo{max-width:150px;height:auto}
    .custom-progress{height:8px;background:rgba(255,255,255,.28);border-radius:999px;overflow:hidden}
    .custom-progress__bar{height:100%;background:#fff;border-radius:inherit;transition:width .22s ease}
  `;
  return style;
}

function embeddedStyle(): HTMLStyleElement {
  const style = document.createElement("style");
  style.textContent = embeddedQuestionShadowStyles;
  return style;
}
